import React, { FC } from 'react'

import DetailBox from '../detail-boxes/detail-box'
import { HandCoinIcon } from '~/assets/icons'
import Trend from '~/assets/icons/trend'
import { Deal } from '~/gql/graphql'
import { useInBucket } from '~/hooks/use-in-bucket'

import styles from './styles.module.css'

interface PriceRiseBadgeVariant {
  icon: React.ReactNode
  title?: string
  text: string
  dataAttribute: 'no-price-rise' | 'has-price-rise' | 'promo-price-rise'
}

const NO_PRICE_RISE_VARIANT: PriceRiseBadgeVariant = {
  icon: <HandCoinIcon className={styles.icon} />,
  title: 'No price rise',
  text: 'during contract',
  dataAttribute: 'no-price-rise'
}

const HAS_PRICE_RISE_VARIANT: PriceRiseBadgeVariant = {
  icon: <Trend className={styles.icon} />,
  text: 'Monthly cost may rise during contract',
  dataAttribute: 'has-price-rise'
}

const WILL_PRICE_RISE_VARIANT: PriceRiseBadgeVariant = {
  icon: <Trend className={styles.icon} />,
  text: 'Monthly cost will rise during contract',
  dataAttribute: 'has-price-rise'
}

const promoPriceRiseVariant = (detailsLine: string): PriceRiseBadgeVariant => {
  return {
    icon: <HandCoinIcon className={styles.icon} />,
    title: 'No price rise',
    text: detailsLine,
    dataAttribute: 'promo-price-rise'
  }
}

const providerVariants: Record<string, PriceRiseBadgeVariant> = {
  '12841': NO_PRICE_RISE_VARIANT, // Fibre Zone
  '12834': NO_PRICE_RISE_VARIANT, // Zen Broadband
  '12774': NO_PRICE_RISE_VARIANT, // Hyperoptic
  '12836': NO_PRICE_RISE_VARIANT, // Cuckoo
  '12846': NO_PRICE_RISE_VARIANT, // Trooli
  '12839': NO_PRICE_RISE_VARIANT, // Hull Fibre
  '12844': NO_PRICE_RISE_VARIANT, // Infinics
  '12843': NO_PRICE_RISE_VARIANT, // Link Broadband
  '12845': NO_PRICE_RISE_VARIANT, // Octaplus
  '12840': NO_PRICE_RISE_VARIANT, // Open Fibre
  '12842': NO_PRICE_RISE_VARIANT, // Squirrel Internet
  '12851': NO_PRICE_RISE_VARIANT, // Gigabit Networks
  '12850': NO_PRICE_RISE_VARIANT, // Yayzi,
  '12848': NO_PRICE_RISE_VARIANT, // MTH Networks
  '12852': NO_PRICE_RISE_VARIANT, // YouFibre
  '12828': promoPriceRiseVariant('in 2024'), // Onestream
  '12835': promoPriceRiseVariant('in 2024'), // Community Fibre
  '12833': promoPriceRiseVariant('in 2024'), // Shell Energy Broadband
  '12179': promoPriceRiseVariant('in 2024'), // Virgin Media
  '12193': promoPriceRiseVariant('in 2024'), // Vodafone
  '12153': promoPriceRiseVariant('in 2024'), // BT
  '12185': promoPriceRiseVariant('in 2024'), // Plusnet
  '12165': promoPriceRiseVariant('in 2024') // TalkTalk
}

const willRiseProviders = [
  '12837', // KCOM
  '12191' // Three
]

const PriceRiseBadge: FC<{ deal: Deal }> = ({ deal: { provider } }) => {
  const isMidContractPriceRiseTest = useInBucket('experiment-mid-contract-price-rise-details-badge', 'variant')
  if (!isMidContractPriceRiseTest) return null

  const variant =
    providerVariants[provider.id] ||
    (willRiseProviders.includes(provider.id) ? WILL_PRICE_RISE_VARIANT : HAS_PRICE_RISE_VARIANT)

  return (
    <DetailBox.Row>
      <span className={styles.badge} data-variant={variant.dataAttribute}>
        {variant.icon}
        <div className={styles.content}>
          {!!variant.title && <span className={styles.title}>{variant.title}</span>}
          {variant.text}
        </div>
      </span>
    </DetailBox.Row>
  )
}

export default PriceRiseBadge
